import React, { useMemo, useCallback, useEffect } from 'react';
import { styled, Shape, CommonStyles, SubTitle, Body, Button, Portal } from '@teko/ds-foundation';
import { randomString } from '../../utils/common.es.js';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1000;
`;
const AlertWrapper = styled(Shape)(({ width, show, theme }) => [
  CommonStyles,
  `
  width: ${width ? `${width}px` : "30%"};
  min-width: ${width ? "unset" : "500px"};
  background-color: ${theme == null ? void 0 : theme.colors.white};
  ${show ? `
      animation: openModal .3s cubic-bezier(0.165, 0.840, 0.440, 1.000) forwards;
      @keyframes openModal {
        0% {
          transform:scale(0);
          opacity: 0;
        }
        100% {
          transform:scale(1);
          opacity: 1;
        }
      };
      ` : `
      animation: closeModal .3s cubic-bezier(.17,.67,.83,.67) forwards;
      @keyframes closeModal {
        0% {
          transform:scale(1);
          opacity: 1;
        }
        30% {
          transform:scale(1);
          opacity: 1;
        }
        100% {
          transform:scale(0);
          opacity: 0;
        }
      };
      `}
  `
]);
const AlertTitle = styled(SubTitle)``;
const AlertContent = styled(Body)(({ theme }) => `
    margin-top: ${theme == null ? void 0 : theme.spacing.s8};
  `);
const AlertFooter = styled.div(({ theme }) => `
    display: flex;
    flex-direction: row-reverse;
    margin-top: ${theme == null ? void 0 : theme.spacing.s24};
  `);
const StyledButton = styled(Button)(({ theme }) => `
  margin-left: ${theme == null ? void 0 : theme.spacing.s24};
  margin-bottom: ${theme == null ? void 0 : theme.spacing.s0};
  &:last-child {
    margin-bottom: ${theme == null ? void 0 : theme.spacing.s0};
    margin-left: ${theme == null ? void 0 : theme.spacing.s0};
  };
`);
const Alert = (_a) => {
  var _b = _a, {
    children,
    show,
    handleClose,
    closeOnClickOutside = true,
    title,
    content,
    primaryAction,
    primaryText,
    secondaryAction,
    secondaryText,
    link,
    linkText,
    linkAction,
    width,
    zIndex,
    destroyOnClose,
    closeOnEscape = false
  } = _b, rest = __objRest(_b, [
    "children",
    "show",
    "handleClose",
    "closeOnClickOutside",
    "title",
    "content",
    "primaryAction",
    "primaryText",
    "secondaryAction",
    "secondaryText",
    "link",
    "linkText",
    "linkAction",
    "width",
    "zIndex",
    "destroyOnClose",
    "closeOnEscape"
  ]);
  const PREFIX_ALERT_OVERLAY_ID = "alert-overlay-";
  const alertOverlayId = useMemo(() => {
    return randomString(PREFIX_ALERT_OVERLAY_ID);
  }, []);
  const onKeyDown = useCallback((event) => {
    if (event.keyCode === 13) {
      primaryAction();
    }
    if (event.keyCode === 27 && !closeOnEscape) {
      handleClose();
    }
  }, [primaryAction, handleClose, closeOnEscape]);
  useEffect(() => {
    if (!show) {
      return;
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onKeyDown, show]);
  useEffect(() => {
    if (destroyOnClose) {
      return () => {
        const alertEl = document.getElementById(alertOverlayId);
        alertEl && alertEl.remove();
      };
    }
  }, [alertOverlayId, destroyOnClose]);
  return /* @__PURE__ */ React.createElement(Portal, {
    show,
    zIndex,
    id: alertOverlayId
  }, /* @__PURE__ */ React.createElement(Backdrop, {
    onClick: closeOnClickOutside ? handleClose : void 0
  }), /* @__PURE__ */ React.createElement(AlertWrapper, __spreadValues({
    borderRadius: "m",
    shadow: "down-m",
    padding: "s24",
    width,
    show
  }, rest), /* @__PURE__ */ React.createElement(AlertTitle, {
    level: 1
  }, title), /* @__PURE__ */ React.createElement(AlertContent, {
    level: 1
  }, content), children, /* @__PURE__ */ React.createElement(AlertFooter, null, primaryAction && primaryText && /* @__PURE__ */ React.createElement(StyledButton, {
    onClick: primaryAction,
    variant: "primary"
  }, primaryText), secondaryAction && secondaryText && /* @__PURE__ */ React.createElement(StyledButton, {
    onClick: secondaryAction,
    variant: "ghost"
  }, secondaryText), (link || linkText) && /* @__PURE__ */ React.createElement(StyledButton, {
    variant: "link",
    href: link,
    onClick: linkAction
  }, linkText || link))));
};

export { Alert };
